import React, { Fragment, useEffect, useRef, useState } from "react";
import { Alert, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { ArrowBack, CloudUpload, Save } from "@mui/icons-material";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import { uploadImage, deleteImageAws } from "../../utils/awsUtils";
import { useUser } from "../context/UserContext";

const UpdateGallery = () => {
  const params = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const { userRole, loading: roleLoading } = useUser();

  const [title, setTitle] = useState("");
  const [currentImgSrc, setCurrentImgSrc] = useState("");
  const [currentImgId, setCurrentImgId] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!roleLoading && userRole === "operator") {
      navigate("/home");
    }
  }, [roleLoading, userRole, navigate]);

  React.useEffect(() => {
    const fetchItem = async () => {
      try {
        const snap = await getDoc(doc(db, "gallery", params.id));
        if (snap.exists()) {
          const d = snap.data();
          setTitle(d.title || "");
          setCurrentImgSrc(d.imgSrc || "");
          setCurrentImgId(d.imgId || "");
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchItem();
  }, [params.id]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErrorMsg("Image must be under 2MB");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setErrorMsg("");
  };

  const handleUpdate = async () => {
    if (!title) return setErrorMsg("Title cannot be empty");

    setLoading(true);
    try {
      let imgSrc = currentImgSrc;
      let imgId = currentImgId;

      if (imageFile) {
        const uploaded = await uploadImage(imageFile);
        if (currentImgId) await deleteImageAws(currentImgId);
        imgSrc = uploaded.imgUrl;
        imgId = uploaded.imgId;
      }

      await updateDoc(doc(db, "gallery", params.id), { title, imgSrc, imgId });

      const userName = auth.currentUser?.email || "Admin";
      await addDoc(collection(db, "notifications"), {
        name: userName,
        message: `Updated Gallery Image: ${title}`,
        createdAt: serverTimestamp(),
      });

      toast("Gallery item updated!", { type: "success" });
      setTimeout(() => navigate("/gallery"), 2000);
    } catch (err) {
      setErrorMsg(err.message || "Failed to update gallery item");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate("/gallery")}
              className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 transition"
            >
              <ArrowBack className="text-gray-400" style={{ fontSize: 20 }} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">Update Gallery Image</h1>
              <p className="text-gray-400 text-xs font-abel mt-0.5">Edit title or replace image</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8">
            {errorMsg && <Alert severity="error" className="mb-5" onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrorMsg(""); }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                />
              </Grid>

              <Grid item xs={12}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">
                  Image <span className="text-gray-300 normal-case">(leave empty to keep current)</span>
                </label>
                {(imagePreview || currentImgSrc) && (
                  <img
                    src={imagePreview || currentImgSrc}
                    alt="preview"
                    className="h-40 w-40 object-cover rounded-xl border border-gray-100 mb-3"
                  />
                )}
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-[#000C66] transition"
                  onClick={() => fileRef.current.click()}
                >
                  <CloudUpload className="text-gray-300 mx-auto" style={{ fontSize: 32 }} />
                  <p className="text-xs text-gray-400 font-abel mt-2">
                    {imageFile ? imageFile.name : "Click to replace image (max 2MB)"}
                  </p>
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
              </Grid>

              <Grid item xs={12} className="flex justify-end">
                <button
                  onClick={handleUpdate}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#000C66] text-white px-8 py-3 rounded-xl font-abel font-semibold hover:bg-blue-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Save style={{ fontSize: 18 }} />
                  )}
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default UpdateGallery;
